<script setup lang="ts">
const colorMode = useColorMode()
const { searchGroups, searchLinks, searchTerm } = useNavigation()
const { fetchModulesList } = useModules()

const color = computed(() => colorMode.value === 'dark' ? '#020420' : 'white')

const [{ data: navigation }, { data: files }] = await Promise.all([
  useAsyncData('navigation', async () => {
    try {
      const result = await Promise.all([
        queryCollectionNavigation('blog')
      ])
      return result.flat()
    } catch (error) {
      console.error('Error fetching navigation:', error)
      return []
    }
  }, {
    transform: data => data.flat(),
    server: true,
    client: true
  }),
  useLazyAsyncData('search', async () => {
    try {
      const result = await Promise.all([
        queryCollectionSearchSections('blog')
      ])
      return result.flat()
    } catch (error) {
      console.error('Error fetching search sections:', error)
      return []
    }
  }, {
    server: false,
    transform: data => data.flat()
  })
])

useHead({
  titleTemplate: title => title ? `${title} · 汉晨光` : '汉晨光',
  meta: [
    { key: 'theme-color', name: 'theme-color', content: color },
    { name: 'keywords', content: '视觉' },
    { name: 'author', content: '汉晨光' },
    { name: 'robots', content: 'index, follow' },
    { name: 'googlebot', content: 'index, follow' },
    { name: 'baiduspider', content: 'index, follow' }
  ]
})

if (import.meta.server) {
  useHead({
    meta: [
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'description', content: '汉晨光' },
      { property: 'og:title', content: '汉晨光' },
      { property: 'og:description', content: '汉晨光' },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: 'zh_CN' },
      { name: 'twitter:title', content: '汉晨光' },
      { name: 'twitter:description', content: '汉晨光' },
      { name: 'twitter:card', content: 'summary_large_image' }
    ],
    link: [
      { rel: 'icon', type: 'image/png', href: '/icon.png' },
      { rel: 'canonical', href: 'https://www.hclidar.com' }
    ],
    htmlAttrs: {
      lang: 'zh-CN'
    }
  })
  useSeoMeta({
    ogSiteName: '汉晨光',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterSite: 'hclidar'
  })
}

// Provide with non-null assertion since this is top level app setup
provide('navigation', navigation!)

const route = useRoute()
const heroBackgroundClass = computed(() => route.meta?.heroBackground || '')
const { isLoading } = useLoadingIndicator()
const appear = ref(false)
const appeared = ref(false)
onMounted(() => {
  fetchModulesList()
  setTimeout(() => {
    appear.value = true
    setTimeout(() => {
      appeared.value = true
    }, 1000)
  }, 0)
})
</script>

<template>
  <UApp>
    <NuxtLoadingIndicator color="var(--ui-primary)" />

    <!-- <UBanner
      id="nuxt-tips-michael"
      title="欢迎光临本网站!"
      icon="i-lucide-wand"
      to="https://baidu.com"
      close
      :actions="[
        {
          label: 'View Tips',
          color: 'neutral',
          variant: 'outline',
          trailingIcon: 'i-lucide-arrow-right',
          to: 'https://baidu.com'
        }
      ]"
    /> -->

    <AppHeader />

    <UMain class="relative">
      <HeroBackground
        class="absolute w-full -top-px transition-all text-primary shrink-0 -z-10"
        :class="[
          isLoading ? 'animate-pulse' : (appear ? heroBackgroundClass : 'opacity-0'),
          appeared ? 'duration-[400ms]' : 'duration-1000'
        ]"
      />

      <NuxtPage />
    </UMain>

    <AppFooter />

    <ClientOnly>
      <LazyUContentSearch
        v-model:search-term="searchTerm"
        :files="files"
        :navigation="navigation"
        :groups="searchGroups"
        :links="searchLinks"
        :fuse="{ resultLimit: 42 }"
      />
    </ClientOnly>
  </UApp>
</template>
